// Requêtes et libellés des rendez-vous : agendas de la collectivité, rendez-vous d'un jour
// (GET /rendez-vous?date=&service=&agenda=), créneaux publics d'une semaine, utilitaires de dates.
import type { Agenda } from '@cc/contrats';
import { useQueries, useQuery } from '@tanstack/react-query';
import { requete } from '../../api/client';
import { useCollectivite } from '../../api/requetes';
import { isoJour } from '../../commun/format';

export type AgendaComplet = Agenda & { actif: boolean };

export interface RendezVous {
  id: string;
  agenda: { id: string; libelle: string; lieu: string | null };
  service: { id: string; nom: string };
  debut: string;
  fin: string;
  etat: string;
  objet: string | null;
  dossier_id: string | null;
  reporte_vers_id: string | null;
  demandeur: { usager_compte_id: string | null; nom: string | null };
}

export interface Creneau {
  debut: string;
  fin: string;
  places_restantes: number;
}

export const LIBELLES_ETAT_RDV: Record<string, string> = {
  reserve: 'Réservé',
  confirme: 'Confirmé',
  present: 'Présent',
  absent: 'Absent',
  reporte: 'Reporté',
  annule: 'Annulé',
};

export const JOURS = ['Lundi', 'Mardi', 'Mercredi', 'Jeudi', 'Vendredi', 'Samedi', 'Dimanche'];

export function useAgendas() {
  return useQuery({
    queryKey: ['agendas'],
    queryFn: ({ signal }) => requete<{ elements: AgendaComplet[] }>('/agendas', { signal }).then((r) => r.elements),
    retry: false,
    staleTime: 60_000,
  });
}

export interface FiltresRdv {
  service: string;
  agenda: string;
}

export function useRendezVousDuJour(date: string, filtres: FiltresRdv) {
  return useQuery({
    queryKey: ['rendez-vous', date, filtres],
    queryFn: ({ signal }) => requete<{ date: string; elements: RendezVous[] }>('/rendez-vous', { signal, parametres: { date, service: filtres.service, agenda: filtres.agenda } }),
    retry: false,
  });
}

/** Les sept jours de la semaine du lundi donné. */
export function joursDeLaSemaine(lundi: string): string[] {
  return Array.from({ length: 7 }, (_, i) => {
    const d = new Date(`${lundi}T00:00:00`);
    d.setDate(d.getDate() + i);
    return isoJour(d);
  });
}

export function lundiLocal(date: string): string {
  const d = new Date(`${date}T00:00:00`);
  const jour = d.getDay() === 0 ? 7 : d.getDay();
  d.setDate(d.getDate() - (jour - 1));
  return isoJour(d);
}

export function decalerJours(date: string, n: number): string {
  const d = new Date(`${date}T00:00:00`);
  d.setDate(d.getDate() + n);
  return isoJour(d);
}

export function useRendezVousDeLaSemaine(lundi: string, filtres: FiltresRdv) {
  const jours = joursDeLaSemaine(lundi);
  return useQueries({
    queries: jours.map((date) => ({
      queryKey: ['rendez-vous', date, filtres],
      queryFn: ({ signal }: { signal: AbortSignal }) => requete<{ date: string; elements: RendezVous[] }>('/rendez-vous', { signal, parametres: { date, service: filtres.service, agenda: filtres.agenda } }),
      retry: false,
    })),
  });
}

/** Créneaux libres d'un agenda pour la semaine contenant la date (route publique par le code de la collectivité). */
export function useCreneaux(agendaId: string | null, semaine: string | null) {
  const collectivite = useCollectivite();
  const code = collectivite.data?.code_court;
  return useQuery({
    queryKey: ['creneaux', code, agendaId, semaine],
    queryFn: ({ signal }) => requete<{ agenda: { id: string; libelle: string }; semaine: string; creneaux: Creneau[] }>(`/public/collectivites/${code}/agendas/${agendaId}/creneaux`, { signal, parametres: { semaine } }),
    enabled: Boolean(code && agendaId),
    retry: false,
  });
}

export function heure(iso: string): string {
  return new Date(iso).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit', timeZone: 'Africa/Libreville' });
}

export function libelleJour(date: string): string {
  const t = new Date(`${date}T00:00:00`).toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long' });
  return t.charAt(0).toUpperCase() + t.slice(1);
}
